import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, API } from "../lib/api";

const FontsContext = createContext(null);

export const FontsProvider = ({ children }) => {
  const [fonts, setFonts] = useState([]);

  const register = useCallback((f) => {
    const url = `${API}/fonts/file/${f.storage_path}`;
    try {
      const ff = new FontFace(f.family, `url("${url}")`);
      ff.load().then((loaded) => document.fonts.add(loaded)).catch(() => {});
    } catch (e) {}
    // Also inject an @font-face rule so export engines (html-to-image) can embed it
    try {
      const styleId = `font-face-${f.id}`;
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `@font-face { font-family: '${f.family}'; src: url('${url}'); font-display: swap; }`;
        document.head.appendChild(style);
      }
    } catch (e) {}
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/fonts");
      setFonts(res.data);
      res.data.forEach(register);
    } catch (e) {}
  }, [register]);

  useEffect(() => { load(); }, [load]);

  const upload = useCallback(async (file) => {
    const form = new FormData();
    form.append("file", file);
    const familyName = file.name.replace(/\.[^.]+$/, "");
    const res = await api.post(`/fonts/upload?name=${encodeURIComponent(familyName)}`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    register(res.data);
    setFonts((prev) => [res.data, ...prev]);
    return res.data;
  }, [register]);

  const remove = useCallback(async (id) => {
    await api.delete(`/fonts/${id}`);
    setFonts((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const families = [...new Set(fonts.map((f) => f.family))];

  return (
    <FontsContext.Provider value={{ fonts, families, load, upload, remove }}>
      {children}
    </FontsContext.Provider>
  );
};

export const useFonts = () => useContext(FontsContext) || { fonts: [], families: [], upload: async () => {}, remove: async () => {} };
